import React, { useCallback, useMemo } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { Minus, Plus } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { MAX_COMPETITION_PREDICTED_SCORE } from '../../../src/worldcup/worldcup.constants';
import { scoreStringToSteppableInt } from '../../../src/worldcup/helpers/worldcupPickScoreInput.helper';
import { getMatchCardPickRowStyles } from './MatchCardPickRow.styles';
import {
  MATCH_CARD_PICK_ROW_A11Y,
  MATCH_CARD_PICK_ROW_COPY,
} from './MatchCardPickRow.constants';

export interface MatchCardPickRowProps {
  matchId: string;
  homeTeam: string;
  awayTeam: string;
  prediction: { home: string; away: string };
  onPredictionChange?: (matchId: string, home: string, away: string) => void;
  showMonkeyBadge?: boolean;
}

export const MatchCardPickRow: React.FC<MatchCardPickRowProps> = ({
  matchId,
  homeTeam,
  awayTeam,
  prediction,
  onPredictionChange,
  showMonkeyBadge = false,
}) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const styles = useMemo(() => getMatchCardPickRowStyles(theme, themeMode), [theme, themeMode]);

  const canEditPrediction = onPredictionChange != null;
  const homeMasked = prediction.home.trim() === '';
  const awayMasked = prediction.away.trim() === '';
  const homeScoreInt = scoreStringToSteppableInt(prediction.home);
  const awayScoreInt = scoreStringToSteppableInt(prediction.away);

  const setHome = useCallback(
    (v: number) => onPredictionChange?.(matchId, String(v), prediction.away),
    [matchId, onPredictionChange, prediction.away],
  );
  const setAway = useCallback(
    (v: number) => onPredictionChange?.(matchId, prediction.home, String(v)),
    [matchId, onPredictionChange, prediction.home],
  );

  const homeUpDisabled = !canEditPrediction || homeScoreInt >= MAX_COMPETITION_PREDICTED_SCORE;
  const homeDownDisabled = !canEditPrediction || homeMasked || homeScoreInt <= 0;
  const awayUpDisabled = !canEditPrediction || awayScoreInt >= MAX_COMPETITION_PREDICTED_SCORE;
  const awayDownDisabled = !canEditPrediction || awayMasked || awayScoreInt <= 0;

  const minusIconColor = (disabled: boolean) => (disabled ? theme.gray[400] : theme.gray[700]);
  const plusIconColor = (disabled: boolean) => (disabled ? theme.gray[400] : theme.onPrimary);

  const renderMinusPlus = (
    minusDisabled: boolean,
    plusDisabled: boolean,
    teamLabel: string,
    onMinus: () => void,
    onPlus: () => void,
  ) => (
    <View style={styles.btnRow}>
      <Pressable
        disabled={minusDisabled}
        onPress={onMinus}
        accessibilityRole="button"
        accessibilityLabel={MATCH_CARD_PICK_ROW_A11Y.decrease(teamLabel)}
        style={({ pressed, hovered }) => [
          styles.scoreBtn,
          styles.scoreBtnMinus,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          !minusDisabled && hovered && Platform.OS === 'web' && styles.scoreBtnHover,
          pressed && !minusDisabled && styles.scoreBtnPressed,
          minusDisabled && styles.scoreBtnDisabled,
        ]}
      >
        <Minus size={16} strokeWidth={2.5} color={minusIconColor(minusDisabled)} />
      </Pressable>
      <Pressable
        disabled={plusDisabled}
        onPress={onPlus}
        accessibilityRole="button"
        accessibilityLabel={MATCH_CARD_PICK_ROW_A11Y.increase(teamLabel)}
        style={({ pressed, hovered }) => [
          styles.scoreBtn,
          styles.scoreBtnPlus,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          !plusDisabled && hovered && Platform.OS === 'web' && styles.scoreBtnHover,
          pressed && !plusDisabled && styles.scoreBtnPressed,
          plusDisabled && styles.scoreBtnDisabled,
        ]}
      >
        <Plus size={16} strokeWidth={2.5} color={plusIconColor(plusDisabled)} />
      </Pressable>
    </View>
  );

  return (
    <View style={styles.matrix}>
      {showMonkeyBadge && (
        <View style={styles.monkeyBadgeRow}>
          <Text style={styles.monkeyBadge}>{MATCH_CARD_PICK_ROW_COPY.MONKEY_PICK_LABEL}</Text>
        </View>
      )}
      <View style={[styles.gridRow, styles.scoreLineMargin]}>
        <View style={styles.pickCol}>
          <View style={styles.scoreBox}>
            <Text style={styles.scoreNum} accessibilityLabel={MATCH_CARD_PICK_ROW_A11Y.scoreForTeam(homeTeam, homeScoreInt)}>
              {homeMasked ? '-' : homeScoreInt}
            </Text>
          </View>
        </View>
        <View style={styles.pickColMid}>
          <Text style={styles.vsText}>vs</Text>
        </View>
        <View style={styles.pickCol}>
          <View style={styles.scoreBox}>
            <Text style={styles.scoreNum} accessibilityLabel={MATCH_CARD_PICK_ROW_A11Y.scoreForTeam(awayTeam, awayScoreInt)}>
              {awayMasked ? '-' : awayScoreInt}
            </Text>
          </View>
        </View>
      </View>
      <View style={styles.gridRow}>
        <View style={styles.pickCol}>
          {renderMinusPlus(
            homeDownDisabled,
            homeUpDisabled,
            homeTeam,
            () => setHome(Math.max(homeScoreInt - 1, 0)),
            () => setHome(homeMasked ? 0 : Math.min(homeScoreInt + 1, MAX_COMPETITION_PREDICTED_SCORE)),
          )}
        </View>
        <View style={styles.pickColMid} />
        <View style={styles.pickCol}>
          {renderMinusPlus(
            awayDownDisabled,
            awayUpDisabled,
            awayTeam,
            () => setAway(Math.max(awayScoreInt - 1, 0)),
            () => setAway(awayMasked ? 0 : Math.min(awayScoreInt + 1, MAX_COMPETITION_PREDICTED_SCORE)),
          )}
        </View>
      </View>
    </View>
  );
};
